import {MarkRequired} from 'ts-essentials';
import {useSessionId} from '../sessionStore/session.hooks';
import {GuestUserState} from './types';
import {useUser} from './user.hooks';
import {useOptionalUser} from './userOptionalData.hooks';

type MaybeSessionState = ReturnType<typeof useSessionId>['data'];
type MaybeUserState = ReturnType<typeof useUser>['data'];

const hasTokenValue = <T extends unknown>(
  payload?: T,
): payload is T & {token: string} =>
  Boolean(
    payload &&
      'token' in (payload as unknown as {token: string}) &&
      (payload as unknown as {token: string}).token,
  );

/**
 *
 * @returns
 * @deprecated use `useIsAuthenticated` instead
 */
export const useIsLoggedIn = (): boolean => {
  const {data: user} = useUser();

  return hasTokenValue(user);
};

export const useUserToken = (): string | null => {
  const {data: user} = useUser();

  return hasTokenValue(user) ? user.token : null;
};

export const useCustomerId = (): number | null => {
  const {data: optionalUser} = useOptionalUser();
  if (useIsLoggedIn()) {
    // userData.id is typed as a number, but in reality it is a string
    // so we parse it, making sure it will end up either as a number or a null
    return parseInt(optionalUser?.userData?.id as unknown as string, 10);
  }
  return null;
};

type GenericDataLoadableState<T> =
  | {isLoading: true; data: null}
  | {isLoading: false; data: null}
  | {isLoading: false; data: T};

type TokenOrSessionId =
  | {token: string; resolvedId: string}
  | {sessionId: string; resolvedId: string};

export type MaybeTokenOrSessionId =
  | {token: string; resolvedId: string}
  | {sessionId: string; resolvedId: string}
  | null;

/**
 * This function should be used instead of `useSimpleUserTokenOrSessionId` as it properly waits
 * for either the session (not logged in) or customer (logged in) to be loaded before
 * returning either one. The previous implementation simply fell back to guest if the user
 * was not logged in, which caused double requests to be triggered. This hook also returns the
 * loading state which is useful within pages if we need to wait for this data to be available
 *
 * @returns TokenOrSessionId
 * When returned data is defined, it will always have a `resolvedId` property which is useful in cases where you just need a unique identifier and don't care if it's a token or session id
 */
export const useUserTokenOrSessionId =
  (): GenericDataLoadableState<TokenOrSessionId> => {
    const {data: session, isLoading: isSessionLoading} = useSessionId();
    const {data: user, isLoading: isUserLoading} = useUser();

    if (isUserLoading || isSessionLoading) {
      return {isLoading: true, data: null};
    }

    if (!!session && !!user) {
      return {isLoading: false, data: getTokenOrSession(session, user)};
    }

    return {isLoading: false, data: null};
  };

/**
 * @deprecated use `useUserTokenOrSessionId` instead
 */
export const useSimpleUserTokenOrSessionId = (): MaybeTokenOrSessionId => {
  const {data: session} = useSessionId();
  const {data: user} = useUser();

  return getTokenOrSession(session, user);
};

const isGuestLogin = (
  user: MaybeUserState,
): user is MarkRequired<GuestUserState, 'guestToken'> =>
  Boolean((user as GuestUserState)?.guestToken);

const getTokenOrSession = (
  session: MaybeSessionState,
  user: MaybeUserState,
): MaybeTokenOrSessionId => {
  if (isGuestLogin(user)) {
    return {
      token: user.guestToken,
      resolvedId: user.guestToken,
    };
  }
  if (hasTokenValue(user)) {
    return {
      token: user.token,
      resolvedId: user.token,
    };
  }
  if (session?.sessionId) {
    return {
      sessionId: session.sessionId,
      resolvedId: session.sessionId,
    };
  }

  return null;
};

export const useUserEmail = () => {
  const {data} = useOptionalUser();
  return data?.userData?.email;
};

export const useUserFirstName = () => {
  const {data} = useOptionalUser();
  return data?.userData?.firstName;
};

export const useUserGender = () => {
  const {data} = useOptionalUser();
  return data?.userData?.gender;
};

export const useIsGuestUser = () => {
  const {data} = useUser();
  return Boolean((data as GuestUserState)?.guestToken);
};
